/**
 * mediapipeArkit — MediaPipe FaceLandmarker blendshapes -> ARKit-52 (ENG-1107).
 *
 * MediaPipe's blendshape head emits 53 categories: `_neutral` plus the 52
 * ARKit names in camelCase, in ITS OWN (alphabetical) order. This repo's
 * order is `ARKIT_NAMES` (the LiveLink wire order). The two are joined BY
 * NAME, case-insensitively, once per session (`buildCategoryIndex`), and the
 * per-frame copy is a 53-entry loop over a prebuilt Int16 map — no strings,
 * no allocation on the frame path.
 *
 * `TongueOut` has no MediaPipe source (the model does not estimate the
 * tongue) and stays 0; it is the ONE known gap and is exported as such so a
 * consumer can report it. Any OTHER unmatched ARKit name is returned in
 * `missing` — a renamed category must never silently zero a channel.
 *
 * Handedness: MediaPipe's landmark "right" is the SUBJECT's right (soma-x
 * measured its rows 33/133 at GNM's -x), so its blendshape names should
 * already follow ARKit's subject-left convention and `mirror` defaults OFF.
 * That is INFERRED from the landmark measurement, not observed on the
 * blendshapes — verify with a real webcam wink before trusting the default
 * (tests/e2e/manual/face-track-live.spec.js).
 */
import { ARKIT_NAMES } from './arkitNames.js'

/** ARKit channels with no MediaPipe source. Always 0 on the tracked frame. */
export const MP_UNMAPPED = ['TongueOut']

const LOWER_TO_INDEX = new Map(ARKIT_NAMES.map((n, i) => [n.toLowerCase(), i]))

/** Every `[leftIndex, rightIndex]` ARKit pair, derived from the names. */
export const LR_PAIRS = ARKIT_NAMES.flatMap((n, i) => {
  if (!n.endsWith('Left')) return []
  const r = ARKIT_NAMES.indexOf(n.slice(0, -4) + 'Right')
  return r >= 0 ? [[i, r]] : []
})

/**
 * @param {Array<{categoryName: string}>} categories  MediaPipe's list for one face
 * @returns {{ map: Int16Array, missing: string[] }}
 */
export function buildCategoryIndex(categories) {
  const map = new Int16Array(categories.length).fill(-1)
  const seen = new Uint8Array(ARKIT_NAMES.length)
  for (let i = 0; i < categories.length; i++) {
    const name = String(categories[i]?.categoryName ?? '').toLowerCase()
    const idx = LOWER_TO_INDEX.get(name)
    if (idx === undefined) continue
    map[i] = idx
    seen[idx] = 1
  }
  const missing = []
  for (let i = 0; i < ARKIT_NAMES.length; i++) {
    if (!seen[i] && !MP_UNMAPPED.includes(ARKIT_NAMES[i])) missing.push(ARKIT_NAMES[i])
  }
  return { map, missing }
}

/**
 * Copy one frame's scores into `out` by the prebuilt index. `out` is fully
 * overwritten (unmapped channels become 0). Allocation-free.
 * @param {Array<{score: number}>} categories
 * @param {{map: Int16Array}} index
 * @param {Float32Array} out  52 floats
 */
export function applyCategories(categories, index, out) {
  out.fill(0)
  const map = index.map
  const n = Math.min(map.length, categories.length)
  for (let i = 0; i < n; i++) {
    const j = map[i]
    if (j >= 0) out[j] = categories[i].score
  }
  return out
}

/** Swap every Left/Right pair in place. Returns `arr`. An involution. */
export function mirrorLeftRight(arr) {
  for (let k = 0; k < LR_PAIRS.length; k++) {
    const l = LR_PAIRS[k][0], r = LR_PAIRS[k][1]
    const t = arr[l]
    arr[l] = arr[r]
    arr[r] = t
  }
  return arr
}